import { supabase } from '../lib/supabaseClient';
import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar as CalendarIcon,
  Plus,
  PlayCircle,
  Video,
  Loader2,
  X
} from 'lucide-react';

const EventCard = ({ event }) => (
  <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-gray-100 hover:border-navy-200 transition-all">
    <div className="relative">
      <img
        src={event.thumbnail}
        alt={event.title}
        className="w-16 h-16 rounded-lg object-cover"
      />
      <div className={`absolute -top-2 -right-2 p-1.5 rounded-full ${
        event.type === 'Live Session' ? 'bg-red-500' : 'bg-navy-600'
      }`}>
        {event.type === 'Live Session' ? 
          <Video size={12} className="text-white" /> : 
          <PlayCircle size={12} className="text-white" />
        }
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-xs px-2 py-1 rounded-full ${
          event.type === 'Live Session'
            ? 'bg-red-50 text-red-600'
            : 'bg-navy-50 text-navy-600'
        }`}>
          {event.type}
        </span>
      </div>
      <h3 className="font-medium text-navy-900 truncate">{event.title}</h3>
      <p className="text-sm text-gray-600 truncate">{event.description}</p>
      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
        <span className="flex items-center gap-1">
          <Clock size={14} />
          {event.time}
        </span>
        <span className="flex items-center gap-1">
          <CalendarIcon size={14} />
          {event.duration}
        </span>
      </div>
    </div>
  </div>
);

const CreateScheduleModal = ({ isOpen, onClose, currentUser }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('schedules')
        .insert([{
          user_id: currentUser.id,
          title,
          description,
          start_time: new Date(startTime).toISOString(),
          end_time: new Date(endTime).toISOString()
        }])
        .select();

      if (error) throw error;
      
      onClose(data?.[0]);
      setTitle('');
      setDescription('');
      setStartTime('');
      setEndTime('');
    } catch (error) {
      console.error('Error creating schedule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Schedule New Session</h2>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-600"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-600"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-600"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-600"
                required
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => onClose()}
              className="px-6 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Schedule'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 
              'August', 'September', 'October', 'November', 'December'];

const calculateDuration = (start, end) => {
  const diff = end - start;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 0).getDate();
};

const Schedule = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setLoading(true);
        const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

        const { data, error } = await supabase
          .from('schedules')
          .select('*')
          .gte('start_time', startOfMonth.toISOString())
          .lte('start_time', endOfMonth.toISOString())
          .order('start_time', { ascending: true });

        if (error) throw error;
        
        const formattedEvents = data.map(event => ({
          id: event.id,
          title: event.title,
          description: event.description,
          type: event.title.includes('Live') ? 'Live Session' : 'Course',
          time: new Date(event.start_time).toLocaleTimeString('en-US', { 
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          duration: calculateDuration(new Date(event.start_time), new Date(event.end_time)),
          start_time: new Date(event.start_time),
          end_time: new Date(event.end_time),
          thumbnail: "/api/placeholder/64/64"
        }));

        setScheduleEvents(formattedEvents);
      } catch (error) {
        console.error('Error fetching schedules:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [currentMonth]);

  const getMonthDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-navy-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar Section */}
        <div className="lg:w-2/3">
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Calendar Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-navy-900">
                  {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <div className="flex items-center gap-1">
                  <button 
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                  >
                    <ChevronLeft size={18} className="text-gray-600" />
                  </button>
                  <button 
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                  >
                    <ChevronRight size={18} className="text-gray-600" />
                  </button>
                </div>
              </div>
              <button 
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 bg-navy-600 text-white px-4 py-2 rounded-lg hover:bg-navy-700 transition-colors text-sm"
              >
                <Plus size={16} />
                <span>Schedule Session</span>
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {/* Week days header */}
              {weekDays.map(day => (
                <div key={day} className="bg-gray-50 p-3 text-center">
                  <span className="text-sm font-medium text-gray-600">{day}</span>
                </div>
              ))}
              
              {/* Calendar days */}
              {getMonthDays().map((day, index) => {
                const isToday = day === new Date().getDate() && 
                              currentMonth.getMonth() === new Date().getMonth() &&
                              currentMonth.getFullYear() === new Date().getFullYear();
                
                const dayEvents = day ? scheduleEvents.filter(event => 
                  event.start_time.getDate() === day &&
                  event.start_time.getMonth() === currentMonth.getMonth() &&
                  event.start_time.getFullYear() === currentMonth.getFullYear()
                ) : [];

                return (
                  <div
                    key={index}
                    className={`bg-white p-3 min-h-[100px] ${
                      day ? 'hover:bg-gray-50 cursor-pointer' : ''
                    }`}
                  >
                    {day && (
                      <>
                        <div className="relative mb-2">
                          <span className={`text-sm inline-flex items-center justify-center ${
                            isToday 
                              ? 'w-7 h-7 rounded-full bg-navy-600 text-white'
                              : 'text-gray-700'
                          }`}>
                            {day}
                          </span>
                        </div>
                        {dayEvents.map((event, eventIndex) => (
                          <div 
                            key={eventIndex}
                            className="text-xs p-1 mb-1 rounded bg-navy-50 text-navy-700 truncate"
                          >
                            {event.time} - {event.title}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Today's Schedule */}
        <div className="lg:w-1/3">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-navy-900 mb-4">Today's Schedule</h2>
            <div className="space-y-4">
              {scheduleEvents
                .filter(event => {
                  const today = new Date();
                  const eventDate = new Date(event.start_time);
                  return (
                    eventDate.getDate() === today.getDate() &&
                    eventDate.getMonth() === today.getMonth() &&
                    eventDate.getFullYear() === today.getFullYear()
                  );
                })
                .sort((a, b) => a.start_time - b.start_time)
                .map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              {scheduleEvents.filter(event => {
                const today = new Date();
                const eventDate = new Date(event.start_time);
                return (
                  eventDate.getDate() === today.getDate() &&
                  eventDate.getMonth() === today.getMonth() &&
                  eventDate.getFullYear() === today.getFullYear()
                );
              }).length === 0 && (
                <div className="text-center py-8">
                  <CalendarIcon size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">No events scheduled for today</p>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="mt-4 text-navy-600 hover:text-navy-700 text-sm font-medium"
                  >
                    Schedule a session
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Schedule Modal */}
      <CreateScheduleModal 
        isOpen={isCreateModalOpen}
        onClose={(newSchedule) => {
          setIsCreateModalOpen(false);
          if (newSchedule) {
            const formattedEvent = {
              id: newSchedule.id,
              title: newSchedule.title,
              description: newSchedule.description,
              type: newSchedule.title.includes('Live') ? 'Live Session' : 'Course',
              time: new Date(newSchedule.start_time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              }),
              duration: calculateDuration(
                new Date(newSchedule.start_time),
                new Date(newSchedule.end_time)
              ),
              start_time: new Date(newSchedule.start_time),
              end_time: new Date(newSchedule.end_time),
              thumbnail: "/api/placeholder/64/64"
            };
            setScheduleEvents(prev => [...prev, formattedEvent].sort((a, b) => a.start_time - b.start_time));
          }
        }}
        currentUser={currentUser}
      />
    </div>
  );
};

export default Schedule;